/**
 * voice-v8 — Sicherungs-Aufnahme im Voice-Modus (Pre.825, Martins Wunsch:
 * "nichts darf verloren gehen").
 *
 * Problem: Wackelt die Verbindung, spricht der User ins Leere — Gemini
 * bekommt nichts, die Information ist WEG (Live-Vorfall: eine Minute
 * Sprache, null Transkripte). Dieses Modul schneidet das Mikrofon parallel
 * lokal mit; geht die Verbindung verloren, wird die unbestätigte Sprache
 * an /api/voice/recover nachgereicht.
 *
 * P2.5/P3.3 (Audit pre.838): Der Recorder läuft DURCH — kein Stop/Start
 * mehr bei jedem Transkript (das war auf iOS ein AudioSession-Knacks-
 * Risiko). Stattdessen markiert ein Wasserstand (confirmedMs), bis wohin
 * die Sprache vom Server bestätigt ist. Beim Flush geht die GANZE Aufnahme
 * + skip_ms an den Server, der den bestätigten Teil wegschneidet und nur
 * den Rest transkribiert (Container bleibt so immer gültig — auch mp4).
 * Rotation (frischer Recorder) passiert höchstens alle MAX_BUFFER_MS und
 * NUR in einem bestätigten Moment, nie mitten in unbestätigte Sprache.
 * iOS: audio/mp4-Fallback — vorher war der Guard auf iPhone still tot.
 */

import { saveGuardSnapshot, clearGuardSnapshot } from "./voice-guard-store.js";

const MAX_BUFFER_MS = 180_000; // Speicher-Deckel: danach im bestätigten Moment rotieren
const MIN_FLUSH_MS = 4_000; // darunter lohnt kein Nachreichen (Atmer, Klicks)
const OVERLAP_MS = 1_000; // Sicherheitsmarge gegen abgeschnittene Wortgrenzen
// Ticket #182: ab so viel UNBESTÄTIGTER Sprache wird der Indikator amber
// ("aufgenommen, aber noch nicht vom Server bestätigt"). Bewusst weit über
// Geminis ~30s-Transkript-Takt — sonst Fehlalarm mitten im Satz (Lehre
// pre.831, der 12s-Stall-Wächter wurde genau deshalb entfernt).
const PENDING_MS = 45_000;
// pre.949: Der IndexedDB-Spiegel wird gedrosselt geschrieben — NICHT jede
// Recorder-Sekunde den ganzen Blob (auf dem Handy zu viel I/O). Der Zustand
// (onState) bleibt 1s-genau; nur die Sicherung läuft im MIRROR_MS-Takt.
const MIRROR_MS = 5_000;
const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

/**
 * Zustand der Sicherungs-Aufnahme für die Sicht-Rückmeldung:
 *  - off:     Mikro zu / nichts läuft
 *  - live:    nimmt auf, zuletzt bestätigt (Rückstand klein)
 *  - pending: nimmt auf, aber seit PENDING_MS nichts bestätigt → könnte hängen
 */
type GuardState = "off" | "live" | "pending";

export class VoiceGuard {
  private rec: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private mime = "";
  private startedAt = 0;
  private confirmedMs = 0;
  private muted = false;
  private flushing = false;
  // True when stop() was called while flushIfLost() was running — deferred cleanup.
  private pendingStop = false;
  // pre.949: Drosselung des IndexedDB-Spiegels (siehe MIRROR_MS).
  private lastMirrorAt = 0;
  /** UI-Hook: ehrlicher Zustand des Aufnahme-Indikators (Ticket #182). */
  onState: (state: GuardState) => void = () => undefined;

  get isActive(): boolean {
    return this.rec !== null && this.rec.state === "recording";
  }

  start(track: MediaStreamTrack): void {
    this.stopRecorderOnly();
    try {
      this.stream = new MediaStream([track]);
      this.beginRecorder();
    } catch {
      // Kein MediaRecorder-Support → Guard still aus (Feature, kein Muss).
      this.stream = null;
    }
  }

  private beginRecorder(): void {
    if (!this.stream || this.muted) return;
    try {
      const mime = MIMES.find((m) => MediaRecorder.isTypeSupported(m));
      if (!mime) return;
      this.mime = mime;
      const rec = new MediaRecorder(this.stream, { mimeType: mime });
      this.rec = rec;
      this.startedAt = Date.now();
      this.confirmedMs = 0;
      this.chunks = [];
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) this.chunks.push(ev.data);
        // 1s-Herzschlag des Recorders mitnutzen: Zustand melden + spiegeln —
        // kein eigener Timer nötig.
        this.tick();
      };
      rec.start(1000);
      this.onState("live");
    } catch {
      this.rec = null;
    }
  }

  /** Unbestätigte Sprache seit Start (0, wenn keine Aufnahme läuft). */
  private unconfirmedMs(): number {
    return this.isActive ? Date.now() - this.startedAt - this.confirmedMs : 0;
  }

  /** Pro Recorder-Sekunde: Zustand melden und Spiegel in IndexedDB führen. */
  private tick(): void {
    const lag = this.unconfirmedMs();
    this.onState(lag >= PENDING_MS ? "pending" : "live");
    // Nur sichern, was sich zu sichern lohnt — selbe Schwelle wie der Flush.
    if (lag < MIN_FLUSH_MS || this.chunks.length === 0) return;
    // pre.949: gedrosselt — höchstens alle MIRROR_MS in IndexedDB spiegeln.
    const now = Date.now();
    if (now - this.lastMirrorAt < MIRROR_MS) return;
    this.lastMirrorAt = now;
    void saveGuardSnapshot({
      blob: new Blob(this.chunks, { type: this.mime || "audio/webm" }),
      mime: this.mime || "audio/webm",
      skipMs: Math.max(0, this.confirmedMs - OVERLAP_MS),
      reason: "tab-tod",
      ts: Date.now(),
    });
  }

  private stopRecorderOnly(): void {
    try {
      if (this.rec && this.rec.state !== "inactive") this.rec.stop();
    } catch {
      /* egal */
    }
    this.rec = null;
    this.lastMirrorAt = 0;
    this.onState("off");
  }

  /** Ein User-Transkript ist angekommen → alles bis jetzt ist ERFASST. */
  noteTranscript(): void {
    if (!this.isActive) return;
    // P3.3: nur den Wasserstand setzen — KEIN Recorder-Neustart mehr.
    this.confirmedMs = Date.now() - this.startedAt;
    // Bestätigt = die Live-Mitschrift hat den Stand → Spiegel ist obsolet.
    void clearGuardSnapshot();
    this.onState("live");
    // Speicher begrenzen: höchstens alle MAX_BUFFER_MS EIN frischer
    // Recorder, und nur jetzt, direkt nach einer Bestätigung.
    if (this.confirmedMs > MAX_BUFFER_MS) {
      this.stopRecorderOnly();
      this.beginRecorder();
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) {
      this.stopRecorderOnly();
      this.chunks = [];
      this.confirmedMs = 0;
      void clearGuardSnapshot();
    } else if (this.stream) {
      this.beginRecorder();
    }
  }

  /** Verbindung verloren/gestört → unbestätigten Rest nachreichen. */
  async flushIfLost(reason: string): Promise<boolean> {
    if (this.flushing) return false;
    if (this.unconfirmedMs() < MIN_FLUSH_MS || this.chunks.length === 0)
      return false;
    this.flushing = true;
    try {
      // Recorder sauber beenden, damit der letzte Chunk komplett ist.
      const rec = this.rec;
      if (rec?.state === "recording") {
        await new Promise<void>((resolve) => {
          rec.onstop = () => resolve();
          try {
            rec.requestData();
            rec.stop();
          } catch {
            resolve();
          }
        });
      }
      this.rec = null;
      this.onState("off");
      const blob = new Blob(this.chunks, { type: this.mime || "audio/webm" });
      const confirmed = this.confirmedMs;
      this.chunks = [];
      this.confirmedMs = 0;
      // Live-Flush trägt diese Sprache jetzt → IndexedDB-Spiegel ist obsolet.
      void clearGuardSnapshot();
      if (blob.size < 2000) return false;
      const ext = this.mime.includes("mp4") ? "m4a" : "webm";
      const form = new FormData();
      form.append("audio", blob, `sicherung.${ext}`);
      form.append("reason", reason);
      // Server schneidet den bestätigten Teil weg (ffmpeg -ss) — sonst
      // landen Dubletten bereits transkribierter Sprache im Verlauf.
      form.append("skip_ms", String(Math.max(0, confirmed - OVERLAP_MS)));
      const res = await fetch("/api/voice/recover", {
        method: "POST",
        credentials: "same-origin",
        body: form,
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        transcript?: string;
      } | null;
      return !!data?.ok && !!data.transcript;
    } catch {
      return false;
    } finally {
      this.flushing = false;
      // Deferred cleanup from stop() that was called while we were flushing.
      if (this.pendingStop) {
        this.pendingStop = false;
        this.chunks = [];
        this.confirmedMs = 0;
      }
    }
  }

  stop(): void {
    this.stopRecorderOnly();
    // Guard: if flushIfLost() is mid-flight, don't wipe chunks — the async
    // continuation needs them to build the blob. pendingStop defers cleanup.
    if (this.flushing) {
      this.pendingStop = true;
    } else {
      this.chunks = [];
      this.confirmedMs = 0;
      void clearGuardSnapshot();
    }
    this.stream = null;
  }
}
